import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Pencil } from 'lucide-react';
import { useLiveEditorScope } from '../../context/LiveEditorScopeContext';
import UniversalBubbleMenu from './UniversalBubbleMenu';

interface EditableTextProps {
  value: string;
  onChange: (newValue: string) => void;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  className?: string;
  placeholder?: string;
  multiline?: boolean;
  label?: string;
  style?: React.CSSProperties;
  canEdit?: boolean;
}

const cleanSingleLineHtml = (rawHtml: string) => {
  if (!rawHtml) return '';
  if (rawHtml === '<p></p>') return '';
  // If wrapped in single <p>...</p>, strip outer tags so it fits naturally in headings/spans
  if (rawHtml.startsWith('<p>') && rawHtml.endsWith('</p>')) {
    const inner = rawHtml.slice(3, -4);
    if (!inner.includes('<p>') && !inner.includes('</p>')) {
      return inner;
    }
  }
  return rawHtml;
};

export default function EditableText({
  value,
  onChange,
  as: Component = 'div',
  className = '',
  placeholder = 'Klik untuk mengedit teks...',
  multiline = false,
  label,
  style = {},
  canEdit: canEditProp
}: EditableTextProps) {
  const scope = useLiveEditorScope();
  const canEdit = typeof canEditProp === 'boolean' ? canEditProp : scope.canEdit;
  const [isFocused, setIsFocused] = useState(false);
  const lastEmittedValueRef = useRef(value);

  const isMultiline =
    multiline ||
    Component === 'p' ||
    Component === 'div';

  const isLightText =
    className.includes('text-white') ||
    className.includes('text-[#F9F9F9]') ||
    className.includes('text-[#f9f9f9]') ||
    className.includes('text-slate-100') ||
    className.includes('text-gray-100') ||
    className.includes('text-amber-100') ||
    className.includes('text-white/70');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        link: false,
        underline: false,
        codeBlock: false,
        code: false,
        horizontalRule: false
      }),
      Underline,
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: placeholder || 'Klik untuk mengedit...',
        emptyEditorClass: 'is-editor-empty'
      }),
      TiptapLink.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: {
          class: isLightText
            ? 'text-[#E6C687] underline hover:text-[#f3d99f] transition-colors'
            : 'text-brand-wine underline hover:text-brand-wine-deep transition-colors'
        }
      })
    ],
    content: value || '',
    editorProps: {
      handleKeyDown: (view, event) => {
        // If single-line element (headings, spans), Enter blurs editor, Shift+Enter inserts line break
        if (!isMultiline && event.key === 'Enter') {
          if (event.shiftKey) {
            return false;
          }
          event.preventDefault();
          view.dom.blur();
          return true;
        }
        return false;
      },
      attributes: {
        class: `outline-none focus:outline-none min-h-[1em] font-inherit text-inherit ${
          isMultiline
            ? '[&_p]:mb-1 last:[&_p]:mb-0'
            : '[&_p]:m-0 [&_p]:inline [&_p]:font-inherit [&_p]:text-inherit'
        } [&_.is-editor-empty:before]:content-[attr(data-placeholder)] [&_.is-editor-empty:before]:opacity-40 [&_.is-editor-empty:before]:pointer-events-none [&_.is-editor-empty:before]:italic [&_.is-editor-empty:before]:float-left [&_.is-editor-empty:before]:h-0`
      }
    },
    onUpdate: ({ editor }) => {
      let html = editor.getHTML();
      if (html === '<p></p>' || html === '') {
        html = '';
      } else if (!isMultiline) {
        html = cleanSingleLineHtml(html);
      }
      lastEmittedValueRef.current = html;
      onChange(html);
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false)
  });

  // Sync external value updates (e.g. discard draft, API reload, or chapter switcher)
  useEffect(() => {
    if (editor && value !== undefined && value !== lastEmittedValueRef.current) {
      if (editor.getHTML() !== value) {
        editor.commands.setContent(value || '', { emitUpdate: false });
        lastEmittedValueRef.current = value;
      }
    }
  }, [value, editor]);

  if (!canEdit) {
    return (
      <Component
        className={`${className} [text-align:inherit] [&_p]:m-0 [&_p]:inline [&_p]:font-inherit [&_p]:text-inherit`}
        style={style}
      >
        {value ? (
          <span dangerouslySetInnerHTML={{ __html: value }} />
        ) : (
          <span className="opacity-40 italic">{placeholder}</span>
        )}
      </Component>
    );
  }

  const focusStyles = isLightText
    ? `bg-transparent hover:bg-white/10 focus-within:bg-black/30 backdrop-blur-xs border border-dashed ${
        isFocused ? 'border-[#E6C687] ring-2 ring-[#E6C687]/40' : 'border-white/30 hover:border-white/70'
      } caret-[#E6C687]`
    : `bg-transparent hover:bg-brand-wine/[0.05] focus-within:bg-white/70 backdrop-blur-xs border border-dashed ${
        isFocused ? 'border-brand-wine ring-2 ring-brand-wine/20' : 'border-brand-wine/30 hover:border-brand-wine/70'
      } caret-brand-wine`;

  return (
    <div className="relative group w-full max-w-full [text-align:inherit]">
      {label && (
        <span
          className={`absolute -top-3.5 left-1 z-20 text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded shadow opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${
            isLightText ? 'bg-[#1C1817] text-[#E6C687] border border-white/20' : 'bg-brand-wine text-white'
          }`}
        >
          {label}
        </span>
      )}

      {/* Universal TipTap Floating Bubble Menu */}
      <UniversalBubbleMenu
        editor={editor}
        allowHeadings={false}
        allowLists={false}
        allowQuote={false}
        allowColors={true}
        allowLink={true}
      />

      <div
        className={`${className} w-full rounded px-2 py-0.5 transition-all cursor-text [text-align:inherit] ${focusStyles}`}
        style={style}
      >
        <EditorContent editor={editor} />
      </div>

      <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-70 transition-opacity">
        <Pencil className={`w-3.5 h-3.5 ${isLightText ? 'text-[#E6C687]' : 'text-brand-wine'}`} />
      </div>
    </div>
  );
}
